import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronUp, ChevronDown, ChevronsUpDown, AlertTriangle, Users } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useCustomers } from '@/hooks/useCustomers'
import { formatDate, formatRelative, formatGbp, planLabel, EMPTY } from '@/lib/customer-format'
import type { CustomerListItem } from '@/lib/customer-types'
import { HealthCell } from '@/components/customers/HealthCell'
import { TrialStatusBadge } from '@/components/customers/TrialStatusBadge'
import { FounderCallbackBadge } from '@/components/customers/FounderCallbackBadge'

type SortKey = 'company' | 'signup' | 'plan' | 'vehicles' | 'mrr' | 'lastActive'

function sortValue(c: CustomerListItem, key: SortKey): string | number | null {
  switch (key) {
    case 'company':
      return c.companyName?.toLowerCase() ?? ''
    case 'plan':
      return c.plan?.toLowerCase() ?? ''
    case 'vehicles':
      return c.vehicleCount
    case 'mrr':
      return c.mrrPence ?? null
    case 'signup': {
      const t = c.signupDate ? Date.parse(c.signupDate) : NaN
      return Number.isNaN(t) ? null : t
    }
    case 'lastActive': {
      const t = c.lastActivityAt ? Date.parse(c.lastActivityAt) : NaN
      return Number.isNaN(t) ? null : t
    }
  }
}

function SortHeader({
  label,
  sortKey,
  current,
  dir,
  onSort,
  align = 'left',
}: {
  label: string
  sortKey: SortKey
  current: SortKey
  dir: 'asc' | 'desc'
  onSort: (k: SortKey) => void
  align?: 'left' | 'right'
}) {
  const active = current === sortKey
  return (
    <th className={`py-2 pr-4 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 font-label text-[0.65rem] uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
          active ? 'text-primary' : 'text-cmd-outline hover:text-white'
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {active ? (
          dir === 'asc' ? (
            <ChevronUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
        )}
      </button>
    </th>
  )
}

function StateCard({ icon, title, message }: { icon: React.ReactNode; title: string; message: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        {icon}
        <p className="font-label text-sm uppercase tracking-wider text-white/80">{title}</p>
        <p className="max-w-md text-sm text-cmd-outline">{message}</p>
      </CardContent>
    </Card>
  )
}

export function CustomersList() {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useCustomers()
  const [sortKey, setSortKey] = useState<SortKey>('company')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(k)
      setDir(k === 'company' || k === 'plan' ? 'asc' : 'desc')
    }
  }

  const sorted = useMemo(() => {
    const rows = data?.customers ?? []
    return [...rows].sort((a, b) => {
      const av = sortValue(a, sortKey)
      const bv = sortValue(b, sortKey)
      if (av === null && bv === null) return 0
      if (av === null) return 1 // nulls always last
      if (bv === null) return -1
      const r =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv))
      return dir === 'asc' ? r : -r
    })
  }, [data?.customers, sortKey, dir])

  // ── States ──
  if (isLoading) {
    return (
      <Card>
        <CardContent className="space-y-2 py-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full" />
          ))}
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <StateCard
        icon={<AlertTriangle className="h-7 w-7 text-cmd-error" aria-hidden="true" />}
        title="Couldn't load customers"
        message="Something went wrong reaching the runtime. Refresh to retry."
      />
    )
  }

  if (data && !data.available) {
    return (
      <StateCard
        icon={<AlertTriangle className="h-7 w-7 text-[#ffd080]" aria-hidden="true" />}
        title="Customer data unavailable"
        message="The TMAgent product bridge is unreachable right now, so the customer list can't be shown. It will reappear automatically once the bridge is back."
      />
    )
  }

  if (sorted.length === 0) {
    return (
      <StateCard
        icon={<Users className="h-7 w-7 text-cmd-outline" aria-hidden="true" />}
        title="No customers yet"
        message="When tenants sign up in the product, they'll appear here."
      />
    )
  }

  return (
    <Card>
      <CardContent className="py-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cmd-highest">
                <SortHeader label="Company" sortKey="company" current={sortKey} dir={dir} onSort={onSort} />
                <SortHeader label="Signed up" sortKey="signup" current={sortKey} dir={dir} onSort={onSort} />
                <SortHeader label="Trial / Plan" sortKey="plan" current={sortKey} dir={dir} onSort={onSort} />
                <SortHeader label="Vehicles" sortKey="vehicles" current={sortKey} dir={dir} onSort={onSort} align="right" />
                <th className="py-2 pr-4 text-left font-label text-[0.65rem] uppercase tracking-wider text-cmd-outline">
                  Health
                </th>
                <SortHeader label="MRR" sortKey="mrr" current={sortKey} dir={dir} onSort={onSort} align="right" />
                <SortHeader label="Last active" sortKey="lastActive" current={sortKey} dir={dir} onSort={onSort} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((c, i) => {
                const to = `/dashboard/customers/${encodeURIComponent(c.tenantId)}`
                return (
                  <tr
                    key={c.tenantId}
                    onClick={() => navigate(to)}
                    className={`cursor-pointer border-b border-cmd-highest/50 transition-colors hover:bg-cmd-highest/30 ${
                      i % 2 === 0 ? 'bg-cmd-surface' : 'bg-cmd-low'
                    }`}
                  >
                    <td className="py-2.5 pr-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to={to}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-white hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                        >
                          {c.companyName || EMPTY}
                        </Link>
                        {c.founderCallbackRequested === true && <FounderCallbackBadge size="sm" />}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-cmd-outline">
                      {formatDate(c.signupDate)}
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="text-white/80">{planLabel(c.plan)}</span>
                        <TrialStatusBadge status={c.trialStatus} daysRemaining={c.trialDaysRemaining} />
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 text-right font-mono tabular-nums text-white/80">
                      {c.vehicleCount}
                    </td>
                    <td className="py-2.5 pr-4">
                      <HealthCell health={c.health} />
                    </td>
                    <td className="py-2.5 pr-4 text-right font-mono tabular-nums text-white/80">
                      {formatGbp(c.mrr)}
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-cmd-outline">
                      {formatRelative(c.lastActivityAt)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-4 font-label text-[0.6rem] uppercase tracking-wider text-cmd-outline">
          {sorted.length} customer{sorted.length === 1 ? '' : 's'} · read-only
        </p>
      </CardContent>
    </Card>
  )
}
